import React, { useRef, useEffect } from 'react'
import { TooltipProvider } from '@/components/ui/tooltip'
import { isToday, isWeekend } from '@/lib/calendar-utils'
import RateCell from './RateCell'

interface DayInfo {
  date: string
  dayOfWeek: string
  dayOfMonth: number
}

interface RoomType {
  id: number
  /** The building's name for the room (server): at a combined inventory a name two listed rooms share
   *  carries its listing in brackets, so the row needs no hotel label of its own. */
  name: string
  minRate: number | null
  maxRate: number | null
}

interface ComputationDetails {
  base_rate_amount?: number
  adjustments?: Array<{
    stage: string
    type: string
    value?: number
    description: string
  }>
  [key: string]: unknown
}

interface RateData {
  id: number
  rateAmount: number
  source: string
  stopSell: boolean
  closedToArrival: boolean
  closedToDeparture: boolean
  minStayArrival: number | null
  maxStay: number | null
  computationDetails: ComputationDetails
  /** Clearance depth standing on this row, or null. Threaded through to the cell's badge. */
  clearanceOffsetPct?: number | null
  pushedRateAmount: number | null
  syncStatus: 'synced' | 'pending' | 'never_pushed'
}

interface OccupancyDay {
  booked: number
  available: number
  occupancyPct: number
  pickup7d: number | null
}

interface RateGridProps {
  /** Today AT THE PROPERTY (YYYY-MM-DD) — see calendar-utils#todayIso. */
  today: string
  daysInMonth: DayInfo[]
  roomTypes: RoomType[]
  rates: Record<string, RateData>
  currency: string
  onCellClick?: (date: string, roomTypeId: number) => void
  comparisonData?: Record<string, number | null>
  comparisonMode?: string
  occupancyByDate?: Record<string, OccupancyDay>
  propertyOccupancyByDate?: Record<string, { booked: number; available: number; occupancyPct: number; label?: string | null }>
  adrData?: Record<string, { adr: number | null; lastYearAdr: number | null }>
  /** The property's backend-served canonical path — threaded to the day popup's target link. */
  propertyBasePath?: string
}

function RateGrid({ daysInMonth, roomTypes, rates, currency, onCellClick, comparisonData, comparisonMode, occupancyByDate, propertyOccupancyByDate, adrData, propertyBasePath, today }: RateGridProps) {
  // Hooks must run unconditionally — keep them above any early return.
  const todayRef = useRef<HTMLTableCellElement>(null)
  useEffect(() => {
    todayRef.current?.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'instant' })
  }, [])

  if (roomTypes.length === 0) {
    return (
      <div className="rounded-xl border border-border/60 bg-white p-10 text-center shadow-sm">
        <p className="text-sm text-muted-foreground">No room types found for this property.</p>
      </div>
    )
  }

  return (
    <TooltipProvider delayDuration={800} skipDelayDuration={0}>
      <div className="overflow-x-auto rounded-xl border border-border/60 bg-white shadow-sm">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr>
              <th className="sticky left-0 z-10 min-w-[140px] border-b border-r border-border/40 bg-white px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Room Type
              </th>
              {daysInMonth.map((day) => {
                const weekend = isWeekend(day.dayOfWeek)
                const today = isToday(day.date)
                return (
                  <th
                    key={day.date}
                    ref={today ? todayRef : undefined}
                    className={`min-w-[90px] border-b border-r border-border/30 px-1.5 py-2.5 text-center ${
                      today ? 'bg-primary/5' : weekend ? 'bg-slate-50' : 'bg-white'
                    }`}
                  >
                    <div className={`text-[10px] font-medium ${weekend ? 'text-primary/70' : 'text-muted-foreground'}`}>
                      {day.dayOfWeek}
                    </div>
                    <div className={`text-xs font-semibold ${today ? 'text-primary' : 'text-foreground'}`}>
                      {day.dayOfMonth}
                    </div>
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {roomTypes.map((rt, index) => (
              <tr key={rt.id} className={index % 2 === 0 ? '' : 'bg-slate-50/30'}>
                <td className="sticky left-0 z-10 border-b border-r border-border/40 bg-white px-4 py-2 whitespace-nowrap">
                  <span className="text-[13px] text-foreground font-semibold">
                    {rt.name}
                  </span>
                </td>
                {daysInMonth.map((day) => {
                  const key = `${rt.id}:${day.date}`
                  return (
                    <RateCell
                      today={today}
                      key={key}
                      rate={rates[key]}
                      currency={currency}
                      date={day.date}
                      dayOfWeek={day.dayOfWeek}
                      isWeekend={isWeekend(day.dayOfWeek)}
                      isToday={isToday(day.date)}
                      onClick={onCellClick ? () => onCellClick(day.date, rt.id) : undefined}
                      comparisonValue={comparisonData?.[key] ?? null}
                      comparisonMode={comparisonMode}
                      occupancy={occupancyByDate?.[day.date]}
                      propertyOccupancy={propertyOccupancyByDate?.[day.date]}
                      roomType={rt}
                      adr={adrData?.[day.date]?.adr}
                      lastYearAdr={adrData?.[day.date]?.lastYearAdr}
                      propertyBasePath={propertyBasePath}
                    />
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </TooltipProvider>
  )
}

export default React.memo(RateGrid)
